import React, { useEffect, useRef, useState } from "react";
import Cropper from "cropperjs";
import "cropperjs/dist/cropper.css";
import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";
import { Modal } from "../../../ui";

const ImageCropModal = ({ image, aspectRatio, onCrop, onClose }) => {
  const { t } = useTranslation("whiteLabel");
  const imgRef = useRef(null);
  const cropperRef = useRef(null);
  const [zoomValue, setZoomValue] = useState(50);

  useEffect(() => {
    if (!imgRef.current) return undefined;
    const cropper = new Cropper(imgRef.current, {
      aspectRatio: aspectRatio || NaN,
      dragMode: "move",
      zoomable: false,
      toggleDragModeOnDblclick: false,
      autoCropArea: 1,
    });
    cropperRef.current = cropper;
    return () => cropper.destroy();
  }, [aspectRatio]);

  const handleZoom = (event) => {
    const value = Number(event.target.value);
    cropperRef.current?.scale(value / 50);
    setZoomValue(value);
  };

  const handleRotate = () => cropperRef.current?.rotate(45);

  const handleCrop = () => {
    const cropper = cropperRef.current;
    if (!cropper) return;
    const canvas = cropper.getCroppedCanvas({
      imageSmoothingQuality: "high",
      maxWidth: 2000,
      maxHeight: 2000,
    });
    onCrop(canvas.toDataURL("image/png"));
  };

  return (
    <Modal isOpen onClose={onClose} title={t("branding.cropTitle")}>
      <div className="h-[400px] w-full overflow-hidden rounded-lg">
        <img ref={imgRef} src={image} alt="" className="block max-w-full" />
      </div>

      <div className="mt-5 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <div className="flex w-full items-center gap-2 sm:flex-1">
          <span className="text-sm whitespace-nowrap text-gray-300">
            {t("branding.cropResize")}
          </span>
          <input
            type="range"
            min="0"
            max="100"
            step="0.01"
            value={zoomValue}
            onChange={handleZoom}
            className="h-2 flex-1 cursor-pointer appearance-none rounded-full bg-gray-700 accent-blue-600"
          />
          <span className="text-sm whitespace-nowrap text-gray-300">
            {Math.round(zoomValue * 2)} %
          </span>
        </div>
        <button
          type="button"
          onClick={handleRotate}
          className="flex shrink-0 items-center gap-2 text-sm font-medium text-gray-300 transition-colors hover:text-white"
        >
          {t("branding.cropRotate")}
          <RotateCw className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-6 flex justify-center">
        <button
          type="button"
          onClick={handleCrop}
          className="h-11 rounded-lg bg-blue-600 px-10 text-sm font-medium text-white transition-colors hover:bg-blue-700"
        >
          {t("branding.cropSave")}
        </button>
      </div>
    </Modal>
  );
};

export default ImageCropModal;
